/**
 * 2026-04-25 Phase 32-A.4 — 각인 의식 모달 (간소화 재작성).
 *
 * 설계 원칙:
 *   1. 텍스트 설명 최소. 상태·UI 가 규칙을 대신 말한다.
 *   2. 재료 없는 각인은 선택 불가 (회색·잠금). 진입해서 당황하는 경로 차단.
 *   3. 심장박동·호흡 BGM 제거 (멀미 유발).
 *   4. 확률 바 그래프로 4상태 직관 — 숫자 %p 대신 길이 비율.
 *   5. 보조 아이템은 기본 접힘 — "더 손본다" 펼치기.
 *   6. 한 패널 (engrave 선택 → 징조/확률/보조 → 의식 시작).
 *
 * API 호출 체인 (동일):
 *   start(engrave, material?) → state 받음
 *   selectOmen / addAid / removeAid — 매번 확률 재계산된 state
 *   confirm → 결과
 */
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { tokens } from "@/art/tokens";
import { PrimaryButton } from "@/components/Button/PrimaryButton";
import {
    engraveCeremonyStart, engraveSelectOmen, engraveAddAid, engraveRemoveAid, engraveConfirm,
    fetchEngraveCatalog,
    type CeremonyState, type EngraveCeremonyResult, type OmenType, type AidId,
    type EngraveCatalogEntry,
} from "@/api/endpoints/forge";
import type { InventoryItemView } from "@/api/types";
import { describeApiError } from "@/utils/errorMessages";
import { ENGRAVE_META } from "@/utils/skillLabels";
import { ForgeOutcomeBurst } from "@/components/forge/ForgeOutcomeBurst";
import { BlackoutFlash } from "@/components/forge/BlackoutFlash";
import { resolveEquipmentAuraColor } from "@/components/forge/EquipmentAura";
import { markForgeShimmer } from "@/components/forge/useForgeShimmer";
import { equipmentIcon } from "@/components/forge/equipmentIcon";

interface Props {
    runId: string;
    item: InventoryItemView;
    preselectedEngraveId?: string;
    preselectedMaterialInstanceId?: string | null;
    inventoryItems?: InventoryItemView[];
    /** 2026-04-25: 골드 사전 비교용. 미지정 시 비교 생략. */
    currentGold?: number;
    onClose: () => void;
    onComplete: (result: EngraveCeremonyResult) => void;
}

type Phase = "selecting" | "ritual" | "blackout" | "bursting" | "final" | "error";

const ENGRAVE_OPTIONS: { id: string }[] = Object.keys(ENGRAVE_META).map(id => ({ id }));
const AID_OPTIONS: AidId[] = ["aid.stability_token", "aid.polar_ore", "aid.seal_stone"];

export function EngraveCeremonyModal({
    runId, item, preselectedEngraveId, preselectedMaterialInstanceId,
    inventoryItems = [],
    currentGold,
    onClose, onComplete,
}: Props) {
    const [catalog, setCatalog] = useState<EngraveCatalogEntry[]>([]);
    const catalogById = useMemo(
        () => Object.fromEntries(catalog.map(e => [e.id, e])) as Record<string, EngraveCatalogEntry>,
        [catalog]
    );
    useEffect(() => { fetchEngraveCatalog().then(setCatalog).catch(() => {}); }, []);

    const [phase, setPhase] = useState<Phase>("selecting");
    const [pickedEngrave, setPickedEngrave] = useState<string>(preselectedEngraveId ?? "");
    const [state, setState] = useState<CeremonyState | null>(null);
    const [final, setFinal] = useState<EngraveCeremonyResult | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [showAids, setShowAids] = useState(false);

    const itemName = item.definition?.name ?? "장비";
    const auraColor = resolveEquipmentAuraColor(item.enhancement, item.engraveTier);

    /** 각인별 재료 보유 여부. */
    const materialsByEngrave = useMemo(() => {
        const map: Record<string, InventoryItemView[]> = {};
        for (const inv of inventoryItems) {
            const tags = inv.definition?.tags ?? [];
            if (!tags.includes("engrave-material")) continue;
            for (const tag of tags) {
                if (tag.startsWith("engrave.")) {
                    (map[tag] ??= []).push(inv);
                }
            }
        }
        return map;
    }, [inventoryItems]);

    /** 보조 아이템 보유 여부. aid.* → material.* 매핑. */
    const aidOwned = useMemo(() => {
        const map: Record<AidId, boolean> = {
            "aid.stability_token": false,
            "aid.polar_ore": false,
            "aid.seal_stone": false,
        };
        const aidToMat: Record<AidId, string> = {
            "aid.stability_token": "material.stability_token",
            "aid.polar_ore": "material.polar_ore",
            "aid.seal_stone": "material.seal_stone",
        };
        for (const inv of inventoryItems) {
            for (const [aid, matId] of Object.entries(aidToMat) as [AidId, string][]) {
                if (inv.definition?.id === matId && inv.quantity > 0) map[aid] = true;
            }
        }
        return map;
    }, [inventoryItems]);

    // 선택된 각인이 재료 없으면 자동으로 선택 해제
    useEffect(() => {
        if (pickedEngrave && !materialsByEngrave[pickedEngrave]?.length) {
            setPickedEngrave("");
        }
    }, [pickedEngrave, materialsByEngrave]);

    const start = async () => {
        if (!pickedEngrave) {
            setErrorMsg("각인을 선택하세요");
            return;
        }
        // 재료 자동 선택 — 가장 높은 등급 (crystal > refined > standard)
        const mats = materialsByEngrave[pickedEngrave] ?? [];
        const pickMaterial = preselectedMaterialInstanceId
            ?? pickBestMaterial(mats)
            ?? null;
        if (!pickMaterial) {
            setErrorMsg("이 각인에 쓸 재료가 없습니다");
            return;
        }
        setBusy(true);
        try {
            const res = await engraveCeremonyStart(runId, item.instanceId, pickedEngrave, pickMaterial);
            setState(res);
            setPhase("ritual");
            setErrorMsg(null);
        } catch (e) {
            const d = describeApiError(e);
            setErrorMsg(`${d.title}: ${d.message}`);
        } finally {
            setBusy(false);
        }
    };

    const pickOmen = async (omen: OmenType) => {
        if (!state || busy) return;
        setBusy(true);
        try {
            const res = await engraveSelectOmen(runId, state.sessionId, omen);
            setState(res);
        } catch (e) {
            const d = describeApiError(e);
            setErrorMsg(`${d.title}: ${d.message}`);
        } finally {
            setBusy(false);
        }
    };

    const toggleAid = async (aid: AidId) => {
        if (!state || busy) return;
        setBusy(true);
        try {
            const has = state.aids.includes(aid);
            const res = has
                ? await engraveRemoveAid(runId, state.sessionId, aid)
                : await engraveAddAid(runId, state.sessionId, aid);
            setState(res);
        } catch (e) {
            const d = describeApiError(e);
            setErrorMsg(`${d.title}: ${d.message}`);
        } finally {
            setBusy(false);
        }
    };

    const confirm = async () => {
        if (!state || busy) return;
        setBusy(true);
        try {
            const res = await engraveConfirm(runId, state.sessionId);
            setFinal(res);
            if ((res.outcome === "SUCCESS" || res.outcome === "CRIT_SUCCESS") && res.item) {
                markForgeShimmer(res.item.instanceId, "engrave");
            }
            const isCrit = res.outcome === "CRIT_SUCCESS" || res.outcome === "CRIT_FAIL";
            setPhase(isCrit ? "blackout" : "bursting");
            onComplete(res);
        } catch (e) {
            const d = describeApiError(e);
            setErrorMsg(`${d.title}: ${d.message}`);
        } finally {
            setBusy(false);
        }
    };

    // 2026-04-25: 안전 닫기 — selecting/ritual/error/final 단계에서만.
    //   blackout/bursting (연출) 중엔 X.
    const canCloseNow = phase === "selecting" || phase === "ritual" || phase === "error" || phase === "final";
    useEffect(() => {
        if (!canCloseNow) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [canCloseNow, onClose]);

    return (
        <div
            style={overlayStyle}
            onClick={canCloseNow ? onClose : undefined}
        >
            <div
                style={modalStyle}
                onClick={(e) => e.stopPropagation()}
            >
                {canCloseNow && (
                    <button style={closeButton} onClick={onClose} aria-label="닫기">✕</button>
                )}
                <h2 style={titleStyle}>각인 · {itemName}</h2>

                {errorMsg && <div style={errorBarStyle}>{errorMsg}</div>}

                {phase === "selecting" && (
                    <SelectingStage
                        item={item}
                        auraColor={auraColor}
                        catalogById={catalogById}
                        materialsByEngrave={materialsByEngrave}
                        pickedEngrave={pickedEngrave}
                        setPickedEngrave={setPickedEngrave}
                        busy={busy}
                        onStart={start}
                        onClose={onClose}
                    />
                )}

                {phase === "ritual" && state && (
                    <RitualStage
                        state={state}
                        catalogById={catalogById}
                        aidOwned={aidOwned}
                        showAids={showAids}
                        setShowAids={setShowAids}
                        busy={busy}
                        currentGold={currentGold}
                        onPickOmen={pickOmen}
                        onToggleAid={toggleAid}
                        onConfirm={confirm}
                    />
                )}

                {phase === "final" && final && (
                    <FinalStage final={final} onClose={onClose} />
                )}
            </div>

            {phase === "blackout" && (
                <BlackoutFlash onComplete={() => setPhase("bursting")} />
            )}

            {phase === "bursting" && final && (
                <ForgeOutcomeBurst
                    outcome={final.outcome}
                    kind="engrave"
                    // 2026-04-25: 결과 후 새 tier 색 우선 (NORMAL → GREAT 승격 시 황금빛 반영)
                    accentColor={final.item
                        ? resolveEquipmentAuraColor(final.item.enhancement, final.item.engraveTier)
                        : auraColor}
                    destroyed={false}
                    onComplete={() => setPhase("final")}
                />
            )}
        </div>
    );
}

// ==================================================================
// SelectingStage — 각인 고르기
// ==================================================================

function SelectingStage({ item, auraColor, catalogById, materialsByEngrave, pickedEngrave, setPickedEngrave, busy, onStart, onClose }: {
    item: InventoryItemView;
    auraColor: string | undefined;
    catalogById: Record<string, EngraveCatalogEntry>;
    materialsByEngrave: Record<string, InventoryItemView[]>;
    pickedEngrave: string;
    setPickedEngrave: (id: string) => void;
    busy: boolean;
    onStart: () => void;
    onClose: () => void;
}) {
    // 2026-04-25: 공용 헬퍼 사용 — RefineModal 과 동일.
    const icon = equipmentIcon(item);
    const picked = pickedEngrave ? catalogById[pickedEngrave] : null;
    // 2026-04-25: 카탈로그 로드 전엔 그리드 자체 안 보임 — tier 색 깜빡 방지
    const catalogLoaded = Object.keys(catalogById).length > 0;
    // 어떤 각인도 재료 없는지
    const anyMaterialAvailable = Object.values(materialsByEngrave).some(arr => arr.length > 0);

    return (
        <div>
            {/* 장비 */}
            <div style={itemRow}>
                <div style={{ ...itemBadge, boxShadow: auraColor ? `0 0 32px ${auraColor}55` : "none" }}>
                    <span style={{ fontSize: 38 }}>{icon}</span>
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, opacity: 0.6, letterSpacing: 2 }}>현재</div>
                    <div style={{ fontSize: 15 }}>
                        +{item.enhancement}
                        {item.engraveId && (
                            <span style={{ opacity: 0.72, marginLeft: 10, fontSize: 13 }}>
                                · {ENGRAVE_META[item.engraveId as keyof typeof ENGRAVE_META]?.label ?? item.engraveId}
                            </span>
                        )}
                        {(item.engraveCracks ?? 0) > 0 && (
                            <span style={{ color: tokens.color.burgundy, marginLeft: 10, fontSize: 12 }}>
                                균열 {item.engraveCracks}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* 카탈로그 로딩 중 안내 */}
            {!catalogLoaded && (
                <div style={{
                    padding: "32px 0", textAlign: "center",
                    color: tokens.color.parchmentDim, fontSize: 13, letterSpacing: 2,
                }}>
                    각인 카탈로그를 부르는 중…
                </div>
            )}

            {/* 어떤 각인도 재료 없으면 안내 */}
            {catalogLoaded && !anyMaterialAvailable && (
                <div style={{
                    padding: "16px 14px", marginBottom: 14,
                    background: `${tokens.color.burgundy}14`,
                    border: `1px solid ${tokens.color.burgundy}55`,
                    borderRadius: 8,
                    color: tokens.color.parchment, fontSize: 13, lineHeight: 1.5,
                    textAlign: "center",
                }}>
                    각인 의식에 쓸 재료가 없습니다. 시나리오·전투에서 재료를 모은 뒤 다시 오세요.
                </div>
            )}

            {/* 각인 그리드 */}
            {catalogLoaded && (
                <div style={engraveGrid}>
                    {ENGRAVE_OPTIONS.map(opt => {
                        const cat = catalogById[opt.id];
                        const meta = ENGRAVE_META[opt.id as keyof typeof ENGRAVE_META];
                        const label = meta?.label ?? opt.id;
                        const hasMat = (materialsByEngrave[opt.id]?.length ?? 0) > 0;
                        const tierColor = tierColorOf(cat?.tier);
                        const selected = pickedEngrave === opt.id;

                        return (
                            <button
                                key={opt.id}
                                onClick={() => hasMat && setPickedEngrave(opt.id)}
                                disabled={!hasMat || busy}
                                style={{
                                    ...engraveButton,
                                    borderColor: selected ? tierColor : `${tierColor}55`,
                                    background: selected
                                        ? `linear-gradient(180deg, ${tierColor}22, transparent)`
                                        : hasMat ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.2)",
                                    opacity: hasMat ? 1 : 0.35,
                                    cursor: hasMat ? "pointer" : "not-allowed",
                                    boxShadow: selected ? `0 0 18px ${tierColor}55, inset 0 0 10px ${tierColor}22` : "none",
                                }}
                            >
                                <div style={{ fontSize: 15, color: tokens.color.parchment }}>
                                    {label}
                                </div>
                                {!hasMat && (
                                    <div style={{ fontSize: 10, opacity: 0.6, marginTop: 2, color: "#ba7" }}>
                                        재료 없음
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* 선택한 각인 미리보기 */}
            {picked && (
                <div style={pickedPreviewStyle}>
                    <div style={{ fontSize: 12, opacity: 0.65, letterSpacing: 2 }}>효과</div>
                    <div style={{ fontSize: 13, lineHeight: 1.5, marginTop: 4 }}>
                        <div>✦ 성공: {picked.successSummary ?? "-"}</div>
                        <div style={{ color: tokens.color.brass, opacity: 0.92 }}>
                            ✦✦ 대성공: {picked.greatSummary ?? "-"}
                        </div>
                    </div>
                </div>
            )}

            {/* 액션 */}
            <div style={actionRow}>
                <button style={ghostButton} onClick={onClose} disabled={busy}>
                    그만둔다
                </button>
                <PrimaryButton onClick={onStart} disabled={busy || !pickedEngrave}>
                    다음 →
                </PrimaryButton>
            </div>
        </div>
    );
}

// ==================================================================
// RitualStage — 징조·보조·확률·의식 시작
// ==================================================================

function RitualStage({ state, catalogById, aidOwned, showAids, setShowAids, busy, currentGold, onPickOmen, onToggleAid, onConfirm }: {
    state: CeremonyState;
    catalogById: Record<string, EngraveCatalogEntry>;
    aidOwned: Record<AidId, boolean>;
    showAids: boolean;
    setShowAids: (b: boolean) => void;
    busy: boolean;
    currentGold?: number;
    onPickOmen: (o: OmenType) => void;
    onToggleAid: (a: AidId) => void;
    onConfirm: () => void;
}) {
    const engrave = catalogById[state.engraveId];
    const engraveLabel = ENGRAVE_META[state.engraveId as keyof typeof ENGRAVE_META]?.label ?? state.engraveId;
    const tierColor = tierColorOf(engrave?.tier);
    // 2026-04-25: 골드 사전 비교
    const goldOk = currentGold === undefined || currentGold >= state.cost;

    return (
        <div>
            <div style={{ textAlign: "center", marginBottom: 14 }}>
                <div style={{ fontSize: 11, opacity: 0.6, letterSpacing: 3 }}>
                    새길 이름
                </div>
                <div style={{ fontSize: 22, color: tierColor, textShadow: `0 0 14px ${tierColor}66`, marginTop: 2, letterSpacing: 4 }}>
                    {engraveLabel}
                </div>
                {/* 2026-04-25: 균열 정보 — 확률에 영향 주므로 RitualStage 에도 노출 */}
                {state.cracks > 0 && (
                    <div style={{ fontSize: 12, color: tokens.color.burgundy, marginTop: 6, letterSpacing: 1 }}>
                        균열 {state.cracks}개 — 대실패 확률 가산
                    </div>
                )}
            </div>

            {/* 확률 바 */}
            <OddsBar odds={state.currentOdds} />

            {/* 징조 */}
            <div style={sectionBlock}>
                <div style={sectionHead}>징조 — 운명을 어느 쪽으로 기울일까</div>
                <div style={omenRow}>
                    {(["HOLD", "BET", "PRESS"] as OmenType[]).map(o => {
                        const active = state.selectedOmen === o;
                        const label = o === "HOLD" ? "묶음" : o === "BET" ? "내기" : "밀기";
                        // 2026-04-25: 풀어쓴 hint — 백엔드 ForgeProbability.applyOmen 동작과 일치.
                        //   HOLD  = 방어   (대실패 → 실패)
                        //   BET   = 공격   (성공 → 대성공/대실패 양극화)
                        //   PRESS = 평균화 (대성공·대실패 → 가운데로)
                        const hint = o === "HOLD" ? "안전하게" : o === "BET" ? "양극단으로" : "양 끝 깎기";
                        return (
                            <button
                                key={o}
                                onClick={() => onPickOmen(o)}
                                disabled={busy}
                                style={{
                                    ...omenButton,
                                    borderColor: active ? tokens.color.brass : `${tokens.color.brass}33`,
                                    background: active ? `${tokens.color.brass}18` : "transparent",
                                    boxShadow: active ? `0 0 16px ${tokens.color.brass}44` : "none",
                                }}
                            >
                                <div style={{ fontSize: 15, color: tokens.color.parchment }}>{label}</div>
                                <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>{hint}</div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* 보조 — 기본 접힘. 2026-04-25: 보유 보조가 하나도 없으면 섹션 자체 숨김 (UI 노이즈 제거). */}
            {(Object.values(aidOwned).some(v => v) || state.aids.length > 0) && (
            <div style={sectionBlock}>
                <button
                    onClick={() => setShowAids(!showAids)}
                    style={aidsDisclosure}
                    disabled={busy}
                >
                    <span>보조 더 손본다 · {state.aids.length}/2</span>
                    <span style={{ opacity: 0.6, fontSize: 11 }}>{showAids ? "▲" : "▼"}</span>
                </button>
                {showAids && (
                    <div style={aidRow}>
                        {AID_OPTIONS.map(aid => {
                            const owned = aidOwned[aid];
                            const active = state.aids.includes(aid);
                            const label = aid === "aid.stability_token" ? "안정석"
                                         : aid === "aid.polar_ore" ? "양극"
                                         : "봉인";
                            const hint = aid === "aid.stability_token" ? "대실패 -5%p"
                                        : aid === "aid.polar_ore" ? "양극 +5%p"
                                        : "대실패 → 실패";
                            return (
                                <button
                                    key={aid}
                                    onClick={() => owned && onToggleAid(aid)}
                                    disabled={busy || !owned}
                                    style={{
                                        ...omenButton,
                                        borderColor: active ? tokens.color.moss : `${tokens.color.brass}33`,
                                        background: active ? `${tokens.color.moss}18` : "transparent",
                                        opacity: owned ? 1 : 0.35,
                                        cursor: owned ? "pointer" : "not-allowed",
                                    }}
                                >
                                    <div style={{ fontSize: 13, color: tokens.color.parchment }}>{label}</div>
                                    <div style={{ fontSize: 10, opacity: 0.6, marginTop: 2 }}>
                                        {owned ? hint : "없음"}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
            )}

            {/* 액션 */}
            <div style={{ marginTop: 18, textAlign: "center" }}>
                <div style={{
                    fontSize: 13, marginBottom: 10,
                    color: goldOk ? tokens.color.parchment : tokens.color.burgundy,
                    opacity: goldOk ? 0.78 : 1,
                }}>
                    🪙 {state.cost.toLocaleString()}
                    {currentGold !== undefined && (
                        <span style={{ opacity: 0.55, marginLeft: 6, fontSize: 12 }}>
                            (보유 {currentGold.toLocaleString()})
                        </span>
                    )}
                </div>
                <PrimaryButton onClick={onConfirm} disabled={busy || !goldOk}>
                    {goldOk ? "🔥 의식 시작" : "🔥 골드 부족"}
                </PrimaryButton>
            </div>
        </div>
    );
}

// ==================================================================
// OddsBar — 4상태 확률 막대 그래프 (숫자 없이 직관)
// ==================================================================

function OddsBar({ odds }: { odds: CeremonyState["currentOdds"] }) {
    // 2026-04-25: 버그 수정 — 백엔드 Odds 는 정수 %p (총합 ≈ 100). 0~1 비율 아님.
    //   total 으로 나눠 0~1 fraction 으로 정규화해야 width 와 label 이 정상 표기됨.
    const total = odds.great + odds.success + odds.fail + odds.critFail;
    if (total <= 0) return null;
    const rows: { label: string; symbol: string; pct: number; color: string }[] = [
        { label: "대성공", symbol: "✦✦", pct: odds.great    / total, color: tokens.color.brass },
        { label: "성공",   symbol: "✦",  pct: odds.success  / total, color: tokens.color.moss },
        { label: "실패",   symbol: "✕",  pct: odds.fail     / total, color: "#998" },
        { label: "대실패", symbol: "☠",  pct: odds.critFail / total, color: tokens.color.burgundy },
    ];
    return (
        <div style={oddsBarWrap}>
            {rows.map(r => (
                <div key={r.label} style={oddsRow}>
                    <div style={{ width: 58, fontSize: 12, color: r.color, letterSpacing: 1 }}>
                        {r.symbol} {r.label}
                    </div>
                    <div style={oddsTrack}>
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${r.pct * 100}%` }}
                            transition={{ duration: 0.45, ease: "easeOut" }}
                            style={{ ...oddsFill, background: r.color }}
                        />
                    </div>
                    <div style={{ width: 40, textAlign: "right", fontSize: 12, color: r.color, fontVariantNumeric: "tabular-nums" }}>
                        {/* 2026-04-25: 0% 오해 방지 — 0이 아닌 미세 확률은 <1% 표기. */}
                        {r.pct === 0 ? "0%" : r.pct < 0.01 ? "<1%" : `${(r.pct * 100).toFixed(0)}%`}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ==================================================================
// FinalStage
// ==================================================================

function FinalStage({ final, onClose }: { final: EngraveCeremonyResult; onClose: () => void }) {
    const color = final.outcome === "CRIT_SUCCESS" ? tokens.color.brass
                 : final.outcome === "SUCCESS" ? tokens.color.moss
                 : final.outcome === "FAIL" ? "#ada"
                 : tokens.color.burgundy;
    const symbol = final.outcome === "CRIT_SUCCESS" ? "✦✦"
                 : final.outcome === "SUCCESS" ? "✦"
                 : final.outcome === "FAIL" ? "✕"
                 : "☠";
    const label = final.outcome === "CRIT_SUCCESS" ? "대성공"
                 : final.outcome === "SUCCESS" ? "성공"
                 : final.outcome === "FAIL" ? "실패"
                 : "대실패";

    const engraveLabel = final.item?.engraveId
        ? (ENGRAVE_META[final.item.engraveId as keyof typeof ENGRAVE_META]?.label ?? final.item.engraveId)
        : null;

    return (
        <div style={{ padding: "20px 4px", textAlign: "center" }}>
            <motion.div
                initial={{ opacity: 0, scale: 0.9, y: -6 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                style={{ fontSize: 40, color, letterSpacing: 6, marginBottom: 12, textShadow: `0 0 20px ${color}66` }}
            >
                {symbol} {label}
            </motion.div>
            {engraveLabel && !final.failureMessage && (
                // 2026-04-25: 각인 이름 scale-in 으로 "이 이름이 새겨졌다" 강조.
                <motion.div
                    initial={{ scale: 0.7, opacity: 0 }}
                    animate={{ scale: [0.7, 1.15, 1], opacity: 1 }}
                    transition={{ duration: 0.55, delay: 0.35, ease: "easeOut" }}
                    style={{ fontSize: 22, color: tokens.color.parchment, marginBottom: 8, textShadow: `0 0 16px ${color}66` }}
                >
                    <strong style={{ color }}>{engraveLabel}</strong>
                    {final.engraveTier === "GREAT" && <span style={{ color: tokens.color.brass, marginLeft: 8, fontSize: 14 }}>✦✦</span>}
                </motion.div>
            )}
            {final.failureMessage && (
                <div style={{ fontSize: 14, opacity: 0.85, marginTop: 4, lineHeight: 1.5 }}>
                    {final.failureMessage}
                </div>
            )}
            {final.cracksAfter && (
                <div style={{ fontSize: 12, color: tokens.color.burgundy, marginTop: 6 }}>
                    균열 {final.cracksAfter}개 누적
                </div>
            )}
            <div style={{ marginTop: 24 }}>
                <PrimaryButton onClick={onClose}>닫기</PrimaryButton>
            </div>
        </div>
    );
}

// ==================================================================
// 헬퍼
// ==================================================================

function pickBestMaterial(mats: InventoryItemView[]): string | null {
    if (mats.length === 0) return null;
    // 우선순위: crystal > refined > standard
    const rank = (inv: InventoryItemView): number => {
        const tags = inv.definition?.tags ?? [];
        if (tags.includes("crystal")) return 3;
        if (tags.includes("refined")) return 2;
        return 1;
    };
    const sorted = [...mats].sort((a, b) => rank(b) - rank(a));
    return sorted[0].instanceId;
}

function tierColorOf(tier: string | undefined): string {
    if (tier === "legendary") return tokens.color.burgundy;
    if (tier === "epic") return "#B565D9";
    if (tier === "rare") return "#5EA4C4";
    if (tier === "uncommon") return tokens.color.moss;
    return tokens.color.brass;
}

// ==================================================================
// 스타일
// ==================================================================

const overlayStyle: React.CSSProperties = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
    position: "relative",
    background: tokens.color.ink,
    color: tokens.color.parchment,
    padding: "36px 48px 32px",
    borderRadius: 14,
    // 2026-04-25: 600 → 820 으로 확대 (데스크톱 양쪽 여백 충분). 4열 각인 그리드도 더 시원하게.
    width: 820,
    maxWidth: "96vw",
    maxHeight: "92vh",
    overflowY: "auto",
    border: `1px solid ${tokens.color.brass}44`,
    boxShadow: `0 24px 80px rgba(0,0,0,0.55), 0 0 32px ${tokens.color.brass}18`,
};

const closeButton: React.CSSProperties = {
    position: "absolute", top: 14, right: 14,
    width: 40, height: 40, borderRadius: 8,
    background: "transparent",
    border: `1px solid ${tokens.color.brass}33`,
    color: tokens.color.parchment, opacity: 0.7, cursor: "pointer",
    fontSize: 16,
};

const titleStyle: React.CSSProperties = {
    fontSize: 18, textAlign: "center",
    color: tokens.color.parchment, opacity: 0.88,
    margin: "0 0 14px 0", letterSpacing: 3, fontWeight: 500,
};

const errorBarStyle: React.CSSProperties = {
    padding: "8px 12px", marginBottom: 12,
    background: `${tokens.color.burgundy}22`,
    border: `1px solid ${tokens.color.burgundy}66`,
    borderRadius: 8, color: tokens.color.burgundy,
    fontSize: 12, textAlign: "center",
};

const itemRow: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: 14,
    padding: "0 4px 12px",
    borderBottom: `1px solid ${tokens.color.brass}22`,
    marginBottom: 14,
};

const itemBadge: React.CSSProperties = {
    width: 64, height: 64, borderRadius: "50%",
    background: "radial-gradient(circle, rgba(255,255,255,0.05), rgba(0,0,0,0.3))",
    border: `1px solid ${tokens.color.brass}55`,
    display: "flex", alignItems: "center", justifyContent: "center",
};

const engraveGrid: React.CSSProperties = {
    display: "grid",
    // 2026-04-25: 11 각인 → 4 열 (3 행 + 마지막 3 칸) 균형. 3 열은 마지막 2 칸이 어색.
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 6,
    marginBottom: 14,
};

const engraveButton: React.CSSProperties = {
    padding: "10px 8px",
    border: `1px solid ${tokens.color.brass}55`,
    borderRadius: 8,
    color: tokens.color.parchment,
    transition: "all 0.18s ease",
    textAlign: "center",
};

const pickedPreviewStyle: React.CSSProperties = {
    padding: "12px 14px",
    background: "rgba(255,255,255,0.03)",
    border: `1px solid ${tokens.color.brass}33`,
    borderRadius: 8,
    marginBottom: 16,
};

const actionRow: React.CSSProperties = {
    display: "flex", justifyContent: "space-between", gap: 10,
    marginTop: 16,
};

const ghostButton: React.CSSProperties = {
    padding: "10px 18px",
    background: "transparent",
    color: tokens.color.parchment,
    border: `1px solid ${tokens.color.brass}44`,
    borderRadius: 10, cursor: "pointer",
    fontSize: 14, letterSpacing: 1, opacity: 0.82,
};

const sectionBlock: React.CSSProperties = {
    marginTop: 14,
};

const sectionHead: React.CSSProperties = {
    fontSize: 11, opacity: 0.62, letterSpacing: 3,
    marginBottom: 8,
};

const omenRow: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 6,
};

const omenButton: React.CSSProperties = {
    padding: "10px 4px",
    borderRadius: 8,
    border: `1px solid ${tokens.color.brass}33`,
    background: "transparent",
    color: tokens.color.parchment,
    cursor: "pointer",
    transition: "all 0.15s ease",
};

const aidsDisclosure: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    padding: "8px 12px",
    background: "transparent",
    border: `1px solid ${tokens.color.brass}22`,
    borderRadius: 8,
    color: tokens.color.parchment,
    cursor: "pointer",
    fontSize: 13,
    letterSpacing: 1,
    marginBottom: 6,
};

const aidRow: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 6,
    marginTop: 4,
};

const oddsBarWrap: React.CSSProperties = {
    background: "rgba(255,255,255,0.03)",
    border: `1px solid ${tokens.color.brass}22`,
    borderRadius: 8,
    padding: "10px 12px",
    marginBottom: 4,
};

const oddsRow: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
};

const oddsTrack: React.CSSProperties = {
    flex: 1,
    height: 8,
    background: "rgba(255,255,255,0.05)",
    borderRadius: 4,
    overflow: "hidden",
};

const oddsFill: React.CSSProperties = {
    height: "100%",
    borderRadius: 4,
};
